import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_COLOR = { pending: "text-orange-600 bg-orange-50", approved: "text-emerald-600 bg-emerald-50", rejected: "text-red-600 bg-red-50", cancelled: "text-slate-500 bg-slate-100" };

export default function Leaves() {
  const qc = useQueryClient();
  const { data: leaves } = useQuery({ queryKey: ["all-leaves"], queryFn: async () => (await api.get("/leaves")).data });

  const decide = async (id, status) => {
    try { await api.put(`/leaves/${id}`, { status }); toast.success(`Leave ${status}`); qc.invalidateQueries({ queryKey: ["all-leaves"] }); qc.invalidateQueries({ queryKey: ["upcoming-leaves"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Leave Requests</h1>
      <div className="lg:hidden space-y-3">
        {(leaves || []).length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-400">No leave requests.</div>}
        {(leaves || []).map((l) => (
          <div key={l.id} data-testid={`leave-card-${l.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{l.employee_name}</div><div className="text-xs text-slate-400 mt-0.5">{l.leave_type}</div></div>
              <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${STATUS_COLOR[l.status]}`}>{l.status}</span>
            </div>
            <div className="mt-2 text-xs text-slate-600">{l.from_date} → {l.to_date} ({l.days}d)</div>
            {l.reason && <div className="mt-1 text-xs text-slate-500">{l.reason}</div>}
            {(l.status === "pending" || l.status === "approved") && (
              <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
                {l.status === "pending" && <>
                  <button data-testid={`approve-leave-m-${l.id}`} onClick={() => decide(l.id, "approved")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm"><Check className="h-4 w-4" /> Approve</button>
                  <button data-testid={`reject-leave-m-${l.id}`} onClick={() => decide(l.id, "rejected")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 text-red-600 text-sm"><X className="h-4 w-4" /> Reject</button>
                </>}
                {l.status === "approved" && <button onClick={() => decide(l.id, "cancelled")} className="flex-1 py-2 rounded-lg bg-slate-50 text-slate-600 text-sm">Cancel Leave</button>}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Type</th>
            <th className="text-left px-4 py-3">Dates</th><th className="text-left px-4 py-3 hidden md:table-cell">Reason</th>
            <th className="text-left px-4 py-3">Status</th><th></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(leaves || []).map((l) => (
              <tr key={l.id} data-testid={`leave-row-${l.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{l.employee_name}</td>
                <td className="px-4 py-3">{l.leave_type}</td>
                <td className="px-4 py-3 text-xs">{l.from_date} → {l.to_date} ({l.days}d)</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{l.reason}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[l.status]}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {l.status === "pending" && <>
                    <button data-testid={`approve-leave-${l.id}`} onClick={() => decide(l.id, "approved")} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"><Check className="h-4 w-4" /></button>
                    <button data-testid={`reject-leave-${l.id}`} onClick={() => decide(l.id, "rejected")} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><X className="h-4 w-4" /></button>
                  </>}
                  {l.status === "approved" && <button onClick={() => decide(l.id, "cancelled")} className="text-xs text-slate-500 hover:underline">Cancel</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
